import keys from 'lodash/keys'
import uniqBy from 'lodash/uniqBy'
import {
  CONTRIBUTION_FIELD_NAMES,
  RECORD_FIELDS_BY_KIND,
  REQUIRED_RECORD_FIELDS,
  type ContributionAmountKind,
  type DocumentProposalKind,
  type DocumentRecordFieldName
} from '@klear/constants/document-processing'
import { childOrderEventIdsOf } from '@klear/schema/document-review-step'
import type {
  DocumentReviewDetailOutput,
  DocumentReviewDoubtView,
  DocumentReviewProposalView
} from '@/app/document-review/use-document-review'

export type ExtractionField = NonNullable<
  DocumentReviewProposalView['extraction'][keyof DocumentReviewProposalView['extraction']]
>

const REFERENCE_FIELDS = ['externalReference', 'externalParentReference'] as const

export type EditableFieldName = DocumentRecordFieldName | (typeof REFERENCE_FIELDS)[number]

type ItemBase = { key: string; needsApproval: boolean; signature: string }

export type TraderItem = ItemBase & { type: 'trader'; doubts: DocumentReviewDoubtView[]; senderUnconfirmed: boolean }
export type OrderItem = ItemBase & {
  type: 'order'
  required: boolean
  createsOrder: boolean
  doubts: DocumentReviewDoubtView[]
}
export type SupplierItem = ItemBase & { type: 'supplier'; proposal: DocumentReviewProposalView }
export type ParentItem = ItemBase & { type: 'parent'; proposal: DocumentReviewProposalView }
export type JoinsItem = ItemBase & { type: 'joins'; proposal: DocumentReviewProposalView }
export type ChildrenItem = ItemBase & { type: 'children'; proposal: DocumentReviewProposalView }
export type FieldItem = ItemBase & {
  type: 'field'
  proposal: DocumentReviewProposalView
  field: EditableFieldName
  value: string | null
  extraction: ExtractionField | null
  doubts: DocumentReviewDoubtView[]
  required: boolean
  editable: boolean
  amountKind?: ContributionAmountKind | null
}
export type DoubtItem = ItemBase & {
  type: 'doubt'
  proposal: DocumentReviewProposalView
  doubt: DocumentReviewDoubtView
}

export type ApprovalItem =
  | TraderItem
  | OrderItem
  | SupplierItem
  | ParentItem
  | JoinsItem
  | ChildrenItem
  | FieldItem
  | DoubtItem

export const TRADER_KEY = 'trader'
export const ORDER_KEY = 'order'

const SIDE_DOUBT = 'trader_side'

export function fieldKey(ordinal: number, field: string): string {
  return `${ordinal}:field:${field}`
}

export function doubtKey(ordinal: number, index: number, kind: string): string {
  return `${ordinal}:doubt:${index}:${kind}`
}

export function valueSignature(value: string | null, amountKind?: ContributionAmountKind | null): string {
  return amountKind === undefined ? (value ?? '') : `${value ?? ''}:${amountKind ?? ''}`
}

function extractionOf(proposal: DocumentReviewProposalView, field: string): ExtractionField | null {
  const extraction = (proposal.extraction as Record<string, ExtractionField | undefined>)[field]
  return extraction ?? null
}

function fieldDoubts(proposal: DocumentReviewProposalView, field: string): DocumentReviewDoubtView[] {
  return proposal.doubts.filter((doubt) => doubt.disposition === 'needs_answer' && doubt.field === field)
}

type FieldSeed = {
  proposal: DocumentReviewProposalView
  field: EditableFieldName
  value: string | null
  required: boolean
  live: boolean
}

function fieldItem({ proposal, field, value, required, live }: FieldSeed): FieldItem {
  const doubts = fieldDoubts(proposal, field)
  const extraction = extractionOf(proposal, field)
  return {
    type: 'field',
    key: fieldKey(proposal.ordinal, field),
    signature: valueSignature(value),
    proposal,
    field,
    value,
    extraction,
    doubts,
    required,
    editable: live,
    needsApproval: live && (value !== null || required || doubts.length > 0)
  }
}

function optionalFieldItems(
  proposal: DocumentReviewProposalView,
  fields: readonly EditableFieldName[],
  read: (field: EditableFieldName) => string | null,
  live: boolean
): FieldItem[] {
  return fields.flatMap((field) => {
    const value = read(field)
    const silent = value === null && extractionOf(proposal, field) === null && fieldDoubts(proposal, field).length === 0
    return silent ? [] : [fieldItem({ proposal, field, value, required: false, live })]
  })
}

function recordFieldItems(proposal: DocumentReviewProposalView, live: boolean): FieldItem[] {
  const record = proposal.record
  if (record === null || record.kind === 'attach_payment') return []
  if (record.kind === 'attach') {
    const contribution = record.contribution
    const updates: Record<string, string | null | undefined> = record.updates ?? {}
    const contributed = optionalFieldItems(
      proposal,
      CONTRIBUTION_FIELD_NAMES.filter((field) => !(field in updates)),
      (field) => (contribution as Record<string, string | null | undefined> | null)?.[field] ?? null,
      live
    ).map((item) => {
      if (item.field !== 'amount') return item
      const amountKind = contribution?.amountKind ?? null
      return { ...item, amountKind, signature: valueSignature(item.value, amountKind) }
    })
    const updated = (keys(updates) as DocumentRecordFieldName[]).map((field) => ({
      ...fieldItem({ proposal, field, value: updates[field] ?? null, required: false, live }),
      needsApproval: live
    }))
    return [...contributed, ...updated]
  }
  const kind = proposal.kind as DocumentProposalKind
  const required: readonly string[] = REQUIRED_RECORD_FIELDS[kind] ?? []
  const fields = record.fields as Record<string, string | null | undefined>
  return (RECORD_FIELDS_BY_KIND[kind] ?? []).map((field) =>
    fieldItem({ proposal, field, value: fields[field] ?? null, required: required.includes(field), live })
  )
}

function referenceItems(proposal: DocumentReviewProposalView, live: boolean): FieldItem[] {
  if (proposal.record === null) return []
  return optionalFieldItems(
    proposal,
    REFERENCE_FIELDS,
    (field) => (field === 'externalReference' ? proposal.externalReference : proposal.externalParentReference),
    live
  )
}

function childrenItems(proposal: DocumentReviewProposalView, live: boolean): ChildrenItem[] {
  const children = childOrderEventIdsOf(proposal.record)
  if (children.length === 0) return []
  return [
    {
      type: 'children',
      key: `${proposal.ordinal}:children`,
      signature: children.join(','),
      proposal,
      needsApproval: live
    }
  ]
}

function linkItems(
  proposal: DocumentReviewProposalView,
  live: boolean
): (SupplierItem | ParentItem | JoinsItem | ChildrenItem)[] {
  const record = proposal.record
  if (record === null || record.kind === 'attach_payment') return []
  if (record.kind === 'attach') {
    return [
      {
        type: 'joins',
        key: `${proposal.ordinal}:joins`,
        signature: `${record.orderEventId}`,
        proposal,
        needsApproval: live
      },
      ...childrenItems(proposal, live)
    ]
  }
  const items: (SupplierItem | ParentItem | ChildrenItem)[] = []
  if (record.supplier !== null || proposal.kind === 'payable') {
    items.push({
      type: 'supplier',
      key: `${proposal.ordinal}:supplier`,
      signature: JSON.stringify(record.supplier),
      proposal,
      needsApproval: live
    })
  }
  if (record.parentOrderEventId !== null || record.parentProposalOrdinal !== null) {
    items.push({
      type: 'parent',
      key: `${proposal.ordinal}:parent`,
      signature: `${record.parentOrderEventId}:${record.parentProposalOrdinal}`,
      proposal,
      needsApproval: live
    })
  }
  return [...items, ...childrenItems(proposal, live)]
}

function doubtItems(
  proposal: DocumentReviewProposalView,
  shownFields: ReadonlySet<string>,
  live: boolean
): DoubtItem[] {
  return proposal.doubts.flatMap((doubt, index) => {
    if (doubt.disposition !== 'needs_answer' || doubt.kind === SIDE_DOUBT || doubt.field === ORDER_KEY) return []
    if (doubt.field !== null && shownFields.has(doubt.field)) return []
    return [
      {
        type: 'doubt' as const,
        key: doubtKey(proposal.ordinal, index, doubt.kind),
        signature: doubt.message,
        proposal,
        doubt,
        needsApproval: live
      }
    ]
  })
}

export function proposalItems(proposal: DocumentReviewProposalView): ApprovalItem[] {
  const live = proposal.orderEventId === null && proposal.paymentId === null
  const fields = [...recordFieldItems(proposal, live), ...referenceItems(proposal, live)]
  const shown = new Set(fields.map((item) => item.field))
  return [...fields, ...linkItems(proposal, live), ...doubtItems(proposal, shown, live)]
}

export function sideDoubts(detail: DocumentReviewDetailOutput): DocumentReviewDoubtView[] {
  return detail.proposals.flatMap((proposal) =>
    proposal.doubts.filter((doubt) => doubt.disposition === 'needs_answer' && doubt.kind === SIDE_DOUBT)
  )
}

export function senderNeedsConfirming(email: NonNullable<DocumentReviewDetailOutput['email']>): boolean {
  return email.verification === 'failed' || (email.verification === 'unsigned' && email.senderDomainTrader !== null)
}

function planDoubtsOn(detail: DocumentReviewDetailOutput, field: 'trader' | 'order'): DocumentReviewDoubtView[] {
  return detail.planDoubts.filter((doubt) => doubt.field === field)
}

function proposalOrderDoubts(detail: DocumentReviewDetailOutput): DocumentReviewDoubtView[] {
  const raised = detail.proposals.flatMap((proposal) =>
    proposal.orderEventId === null
      ? proposal.doubts.filter((doubt) => doubt.disposition === 'needs_answer' && doubt.field === ORDER_KEY)
      : []
  )
  return uniqBy(raised, (doubt) => `${doubt.kind}\u0000${doubt.message}`)
}

export function approvalItems(
  detail: DocumentReviewDetailOutput,
  touched: ReadonlySet<string> = new Set()
): ApprovalItem[] {
  const doubts = [...planDoubtsOn(detail, 'trader'), ...sideDoubts(detail)]
  const orderDoubts = [...planDoubtsOn(detail, 'order'), ...proposalOrderDoubts(detail)]
  const creates = detail.proposals.some(
    (proposal) => proposal.orderEventId === null && proposal.record?.kind === 'create' && proposal.kind !== 'payable'
  )
  const head: ApprovalItem[] = [
    {
      type: 'trader',
      key: TRADER_KEY,
      signature: [detail.traderId, ...doubts.map((doubt) => doubt.message)].join('\u0000'),
      doubts,
      senderUnconfirmed: detail.email !== null && senderNeedsConfirming(detail.email),
      needsApproval: detail.traderId !== null
    },
    {
      type: 'order',
      key: ORDER_KEY,
      signature: JSON.stringify(detail.order),
      required: detail.order === null && creates,
      createsOrder: detail.order?.kind === 'create',
      doubts: orderDoubts,
      needsApproval: detail.order !== null || creates || orderDoubts.length > 0
    }
  ]
  return needingReviewer([...head, ...detail.proposals.flatMap(proposalItems)], touched)
}

export function proposalOrdinal(item: ApprovalItem): number | null {
  return item.type === 'trader' || item.type === 'order' ? null : item.proposal.ordinal
}

function readCleanly(item: ApprovalItem): boolean {
  switch (item.type) {
    case 'trader':
      return item.doubts.length === 0 && !item.senderUnconfirmed
    case 'order':
      return !item.required && !item.createsOrder && item.doubts.length === 0
    case 'field':
      return item.doubts.length === 0 && !(item.required && item.value === null)
    case 'supplier': {
      const record = item.proposal.record
      const supplier = record?.kind === 'create' ? record.supplier : null
      return supplier !== null && supplier.kind !== 'create'
    }
    case 'doubt':
      return false
    default:
      return true
  }
}

function needingReviewer(items: ApprovalItem[], touched: ReadonlySet<string>): ApprovalItem[] {
  const traderInDoubt = items.some((item) => item.type === 'trader' && item.doubts.length > 0)
  return items.map((item) => {
    if (!item.needsApproval) return item
    const clean = !traderInDoubt && readCleanly(item)
    return clean && !touched.has(item.key) ? { ...item, needsApproval: false } : item
  })
}

export function notedDoubts(proposal: DocumentReviewProposalView): DocumentReviewDoubtView[] {
  return proposal.doubts.filter((doubt) => doubt.disposition === 'noted')
}
